/* eslint-disable react-hooks/exhaustive-deps */
import {
  AptunoButton,
  Icon,
  SIZES,
  useResponsive,
  VARIANTS,
} from '@aptuno/aptuno-ui';
import { LineBox, SkeletonLineStyled } from '@components/commons';
import {
  ApplicationId,
  BtnActions,
  EstateApplicationModel,
  useCreditStudyActions,
  useEstateActions,
  useEstateApplicationActions,
  UserId,
  ViewError,
} from '@contexts/Estate';
import { getMainTags } from '@contexts/Estate/Shared';
import {
  useLoggedInUser,
  UserByIdQuery,
  userReadActions,
} from '@shared/Authentication';
import { Response } from '@shared/models';
import { getMainImage, parseImages } from '@shared/utils';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import {
  addCotenantToEstateApplicationErrors,
  creditStudyErrors,
} from '../../errors';
import { CompactEstateCard } from '../CompactEstateCard/CompactEstateCard';
import {
  CompactEstateCardStyled,
  InfoBox,
} from '../CompactEstateCard/CompactEstateCard.styled';
import { EaseInModalError } from '../EaseInModal/EaseInModal';
import {
  ActionsBarRoot,
  Blue,
  HeaderButton,
  TextBox,
  TextSecondary,
  TitleSection,
} from '../EstateApplication.styled';
import {
  CotenantCardDescription,
  CotenantDescriptionImportant,
  CotenantDescriptionParagraph,
  WithCreditStudyWrapper,
} from './AddCotenantScreen.styled';

export const AddCotenantScreen = () => {
  const router = useRouter();
  const user = useLoggedInUser();

  const [showErrorModal, setShowErrorModal] = useState<ViewError | undefined>(
    undefined
  );
  const { isMobile } = useResponsive();

  const { estateListingDetails: estateDetailsData, getEstateListingDetails } =
    useEstateActions().listingDetailsQueryActions.useGetEstateListingDetails();

  const { result: exposedEstateApplication, get: getExposedEstateApplication } =
    useEstateApplicationActions().estateApplciationQueryActions.useGetEstateApplication();

  const { result: providedUserInformation, get: getProvidedUserInformation } =
    useCreditStudyActions().creditStudyQueryActions.useGetProvidedUserInformation();

  const {
    addedCotenant,
    loading: loadingAddCotenant,
    error: errorOnAddCotenant,
    addCotenant,
  } = useEstateApplicationActions().estateApplicationCommandActions.useAddCotenant();

  const { result: userResponse, get: getUserById } =
    userReadActions.useGetUserById();
  const tenantName = Response.isSucceed(userResponse)
    ? `${userResponse.result.user.firstName} ${userResponse.result.user.lastName}`
    : '';

  const { mainFeatures, mainImage } = useMemo(
    () => ({
      mainImage: estateDetailsData && getMainImage(estateDetailsData.media),
      mainFeatures: estateDetailsData
        ? getMainTags(estateDetailsData.characteristics)
        : [],
    }),
    [estateDetailsData]
  );

  useEffect(() => {
    const { estateLegalId, applicationId } = router.query;
    if (estateLegalId && applicationId) {
      getProvidedUserInformation();
      getEstateListingDetails({ estateId: estateLegalId.toString() });
      getExposedEstateApplication({
        estateApplicationId: new ApplicationId(applicationId.toString()),
      });
    }
  }, [router.query?.estateLegalId]);

  useEffect(() => {
    if (Response.isSucceed(exposedEstateApplication))
      getUserById(
        new UserByIdQuery(exposedEstateApplication.result.tenantUserId)
      );
  }, [exposedEstateApplication.type]);

  useEffect(() => {
    if (addedCotenant) router.reload();
  }, [addedCotenant]);

  useEffect(() => {
    if (errorOnAddCotenant) {
      setShowErrorModal(
        addCotenantToEstateApplicationErrors[errorOnAddCotenant.code] || {
          ...creditStudyErrors['DEFAULT'],
          text: errorOnAddCotenant.code,
        }
      );
    }
  }, [errorOnAddCotenant]);

  const handleApplyButton = () => {
    const { applicationId } = router.query;

    const canAddCotenant =
      !!user?.data?.id &&
      applicationId &&
      Response.isSucceed(exposedEstateApplication) &&
      EstateApplicationModel.canAddCotenant(
        exposedEstateApplication.result
      ) &&
      !EstateApplicationModel.isCotenantAlreadyAdded(
        exposedEstateApplication.result,
        new UserId(user.data.id)
      );

    if (canAddCotenant) {
      addCotenant({
        estateApplicationId: new ApplicationId(applicationId.toString()),
      });
    }
  };

  const getActionAfterError = (error: ViewError) => {
    switch (error.btnAction) {
      case BtnActions.GO_BACK:
        handleOnBackClick();
        break;
      case BtnActions.GO_HOME:
        goHome();
        break;
      case BtnActions.GO_NEXT:
        setShowErrorModal(undefined);
        break;
      case BtnActions.KEEP_ON_PAGE:
        setShowErrorModal(undefined);
        break;
      default:
        setShowErrorModal(undefined);
        break;
    }
  };

  const goHome = () =>
    router.push({
      pathname: '/',
    });

  const modal = () => {
    return (
      showErrorModal && (
        <EaseInModalError
          size={600}
          isActive={showErrorModal}
          onClose={() => getActionAfterError(showErrorModal)}
          title={showErrorModal.title}
          onAcceptButtonClick={() => getActionAfterError(showErrorModal)}
          btnText={`${showErrorModal.btnText}`}
          mainText={showErrorModal.text}
          secondaryText={showErrorModal.textSecondary}
        />
      )
    );
  };

  const handleOnBackClick = () => router.push('/perfil/visitas-realizadas');

  return (
    <WithCreditStudyWrapper>
      {showErrorModal && modal()}
      <HeaderButton
        appearance={VARIANTS.SECONDARY}
        isCircular
        onClick={handleOnBackClick}
      >
        <Icon icon="arrowleftalt" />
      </HeaderButton>
      <TitleSection>
        Aplicarás como
        <Blue>coarrendatario.</Blue>
      </TitleSection>
      {estateDetailsData && (
        <CompactEstateCard
          imageUrl={parseImages(mainImage?.uri, 'thumb', 'jpg')}
          title={estateDetailsData.title}
          price={estateDetailsData.price || 0}
          administatrionFee={estateDetailsData.administrationPrice || 0}
          rooms={Number(
            mainFeatures.find((feature) => feature.slug === 'nro-cuartos')
              ?.value || '0'
          )}
          bathrooms={Number(
            mainFeatures.find((feature) => feature.slug === 'nro-banos')
              ?.value || '0'
          )}
          squareMeters={Number(
            mainFeatures.find((feature) => feature.slug === 'area')?.value ||
              '0'
          )}
        />
      )}
      {!estateDetailsData && (
        <CompactEstateCardStyled>
          <LineBox>
            <SkeletonLineStyled
              width={isMobile ? '88px' : '120px'}
              height={isMobile ? '88px' : '120px'}
              borderRadius="1.5rem"
            />
          </LineBox>
          <InfoBox>
            <SkeletonLineStyled
              width={isMobile ? '120px' : '200px'}
              height="24px"
              borderRadius=".5rem"
              margin="5px 0"
            />
            <SkeletonLineStyled
              width={isMobile ? '100px' : '120px'}
              height="20px"
              borderRadius=".5rem"
              margin="5px 0"
            />
            <SkeletonLineStyled
              width={isMobile ? '120px' : '190px'}
              height="24px"
              borderRadius=".5rem"
              margin="5px 0"
            />
          </InfoBox>
        </CompactEstateCardStyled>
      )}
      {Response.isSucceed(providedUserInformation) && (
        <TextBox bgColor="transparent">
          <h2>¡Olvídate de aplicar de nuevo!</h2>
          <TextSecondary>
            Aprovecharemos que ya tenemos un resultado vigente para ver si
            puedes arrendar este nuevo inmueble.
          </TextSecondary>
        </TextBox>
      )}
      <CotenantCardDescription>
        <CotenantDescriptionImportant>
          Serás coarrendatario de <span>{tenantName}</span>
        </CotenantDescriptionImportant>
        <CotenantDescriptionParagraph>
          Como coarrendatario, tu nombre estará en el contrato y apoyarás a un
          tomador a arrendar un inmueble de forma solidaria. Esto quiere decir{' '}
          <strong>
            que en caso tal que el tomador no pueda pagar el cánon de
            arrendamiento, deberás pagar por él.
          </strong>
        </CotenantDescriptionParagraph>
        <CotenantDescriptionParagraph>
          Además, también aplicaremos nuestro estudio de riesgo y crédito sobre
          tu vida crediticia y otras bases de datos para determinar si puedes
          asumir la obligación del tomador a la hora de pagar el canon de
          arrendamiento en caso de mora.
        </CotenantDescriptionParagraph>
      </CotenantCardDescription>
      <ActionsBarRoot>
        <AptunoButton
          disabled={loadingAddCotenant}
          appearance={VARIANTS.PRIMARY}
          size={SIZES.BIGGEST}
          onClick={handleApplyButton}
        >
          Aplicar a este inmueble
          <Icon width="12px" height="12px" icon="Arrow_Right" />
        </AptunoButton>
      </ActionsBarRoot>
    </WithCreditStudyWrapper>
  );
};
